import * as THREE from 'three';
import { MAP } from '../utils/constants';
import { BUILDING_LOT_PAD, isParkLot } from './mapLayout';
import { districtFromCell, districtStyle } from './districts';
import { mulberry32 } from '../utils/rng';

export type WindowLightsHandle = {
  mesh: THREE.InstancedMesh;
  update: (dt: number) => void;
};

type WindowState = {
  on: boolean;
  nextToggle: number;
  brightness: number;
  warmth: number;
};

export function createWindowLights(seed: number = 42): WindowLightsHandle {
  const rng = mulberry32(seed);

  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;

  const windowsPerFloor = 6;
  const floorsPerBuilding = 8;
  const windowsPerBuilding = windowsPerFloor * floorsPerBuilding * 4;

  let buildingCount = 0;
  for (let iz = 0; iz < GRID; iz++) {
    for (let ix = 0; ix < GRID; ix++) {
      if (!isParkLot(ix, iz)) buildingCount++;
    }
  }

  const totalWindows = buildingCount * windowsPerBuilding;
  if (totalWindows === 0) {
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({ visible: false });
    const mesh = new THREE.InstancedMesh(geometry, material, 1);
    mesh.count = 0;
    mesh.visible = false;
    return { mesh, update: () => {} };
  }

  const geometry = new THREE.PlaneGeometry(1.8, 1.2);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffeedd,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const mesh = new THREE.InstancedMesh(geometry, material, totalWindows);
  mesh.frustumCulled = false;

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const windowStates: WindowState[] = [];

  let idx = 0;

  for (let iz = 0; iz < GRID; iz++) {
    for (let ix = 0; ix < GRID; ix++) {
      if (isParkLot(ix, iz)) continue;

      const cx = -HALF + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz = -HALF + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
      const w = blockW - BUILDING_LOT_PAD;

      const district = districtFromCell(cx, cz, HALF);
      const sty = districtStyle(district);
      const height = sty.hMin + rng() * (sty.hMax - sty.hMin);
      const actualFloors = Math.floor(height / 4);

      for (let floor = 0; floor < Math.min(actualFloors, floorsPerBuilding); floor++) {
        const y = 3 + floor * 4;

        for (let side = 0; side < 4; side++) {
          for (let wi = 0; wi < windowsPerFloor; wi++) {
            const offset = (wi - (windowsPerFloor - 1) / 2) * 4;

            let wx = cx;
            let wz = cz;
            let rotY = 0;

            switch (side) {
              case 0:
                wz = cz + w * 0.5 + 0.1;
                wx = cx + offset;
                rotY = 0;
                break;
              case 1:
                wz = cz - w * 0.5 - 0.1;
                wx = cx + offset;
                rotY = Math.PI;
                break;
              case 2:
                wx = cx + w * 0.5 + 0.1;
                wz = cz + offset;
                rotY = Math.PI / 2;
                break;
              case 3:
                wx = cx - w * 0.5 - 0.1;
                wz = cz + offset;
                rotY = -Math.PI / 2;
                break;
            }

            dummy.position.set(wx, y, wz);
            dummy.rotation.set(0, rotY, 0);
            dummy.updateMatrix();
            mesh.setMatrixAt(idx, dummy.matrix);

            const isOn = rng() < 0.4;
            const warmth = 0.8 + rng() * 0.4;

            windowStates.push({
              on: isOn,
              nextToggle: 5 + rng() * 60,
              brightness: isOn ? 0.6 + rng() * 0.4 : 0,
              warmth,
            });

            if (isOn) {
              color.setRGB(warmth, warmth * 0.85, warmth * 0.6);
            } else {
              color.setRGB(0.02, 0.02, 0.03);
            }
            mesh.setColorAt(idx, color);

            idx++;
          }
        }
      }

      while (idx % windowsPerBuilding !== 0) {
        dummy.position.set(0, -1000, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(idx, dummy.matrix);
        windowStates.push({
          on: false,
          nextToggle: 9999,
          brightness: 0,
          warmth: 1,
        });
        color.setRGB(0, 0, 0);
        mesh.setColorAt(idx, color);
        idx++;
      }
    }
  }

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  const update = (dt: number) => {
    let needsUpdate = false;

    for (let i = 0; i < windowStates.length; i++) {
      const state = windowStates[i];
      state.nextToggle -= dt;

      if (state.nextToggle <= 0) {
        state.on = !state.on;
        state.nextToggle = 10 + rng() * 120;
        needsUpdate = true;
      }

      const targetBrightness = state.on ? 0.6 + rng() * 0.4 : 0;
      state.brightness += (targetBrightness - state.brightness) * dt * 3;

      if (state.on && rng() < 0.001) {
        state.brightness *= 0.7;
        needsUpdate = true;
      }
    }

    if (needsUpdate || rng() < 0.1) {
      for (let i = 0; i < windowStates.length; i++) {
        const state = windowStates[i];
        if (state.brightness > 0.01) {
          color.setRGB(
            state.brightness * state.warmth,
            state.brightness * state.warmth * 0.85,
            state.brightness * state.warmth * 0.6,
          );
        } else {
          color.setRGB(0.02, 0.02, 0.03);
        }
        mesh.setColorAt(i, color);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  };

  return { mesh, update };
}
